import React, { useRef, useState } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

// Tap-to-speak search — uses the browser's built-in speech recognition; hidden when unsupported
export function VoiceSearchButton({ onResult, className = "" }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  if (!SR) return null;

  const toggle = () => {
    if (listening) {
      try { recRef.current?.stop(); } catch (e) { console.debug("voice stop:", e?.message); }
      setListening(false);
      return;
    }
    const rec = new SR();
    recRef.current = rec;
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const text = e.results?.[0]?.[0]?.transcript?.trim();
      if (text) onResult(text);
      else toast.error("I didn't catch that — please try again");
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone access is blocked — allow the mic to search by voice");
      } else if (e.error !== "aborted" && e.error !== "no-speech") {
        toast.error("Voice search hit a snag — please try again");
      }
    };
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      console.debug("voice start:", e?.message);
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={listening ? "Stop listening" : "Search by voice"}
      title="Search by voice"
      data-testid="voice-search-btn"
      className={`shrink-0 h-10 w-10 flex items-center justify-center border transition-colors ${
        listening
          ? "border-[#a8842c] bg-[#a8842c]/10 text-[#a8842c]"
          : "border-[#d8cdb5] text-[#5c5240] hover:border-[#a8842c] hover:text-[#a8842c]"
      } ${className}`}
    >
      <Mic size={16} className={listening ? "animate-pulse" : ""} />
    </button>
  );
}
